import {
  queryKeys,
  type UpdateCheckoutPurchaseIntentsByPurchaseIntentIdBody,
  useInvalidateQueries,
  useUpdateCheckoutPurchaseIntentsByPurchaseIntentId,
} from "@birdwatch/client";
import { useQueryClient } from "@tanstack/react-query";

import { useRefresh } from "@/features/shared/realtime-refresh";

import { currentPurchaseIntentQueryKey } from "./use-current-purchase-intent";

export type UpdatePurchaseIntentInput =
  UpdateCheckoutPurchaseIntentsByPurchaseIntentIdBody;

type UpdateInput = {
  purchaseIntentId: string;
  updatePurchaseIntentInput: UpdatePurchaseIntentInput;
};

export const useUpdatePurchaseIntent = () => {
  const queryClient = useQueryClient();
  const refresh = useRefresh();
  const invalidate = useInvalidateQueries();

  // The route also announces `account.changed` for a presale reservation (its
  // acquisition trackings), which no checkout screen shows.
  const mutation = useUpdateCheckoutPurchaseIntentsByPurchaseIntentId({
    mutationKey: ["update-purchase-intent"],
    onSuccess: (_intent, { params }) =>
      void Promise.all([
        refresh("purchaseIntent.changed", params),
        invalidate(
          // The current checkout may be keyed by an empty id — the account's
          // single pending intent — which the event's keys do not name.
          currentPurchaseIntentQueryKey(""),
          // A payment-method switch re-mints the setup intent and moves the
          // account's methods.
          queryKeys.getAccountsPaymentMethodsByAccountId(),
          queryKeys.getCheckoutPurchaseIntentsSetupIntentByPurchaseIntentId(),
        ),
      ]),
  });

  const run = async ({
    purchaseIntentId,
    updatePurchaseIntentInput,
  }: UpdateInput) => {
    const queryKey = currentPurchaseIntentQueryKey(purchaseIntentId);

    // Optimistically reflect a payment-method switch so the radio doesn't
    // snap back while the request is in flight.
    if (updatePurchaseIntentInput.paymentMethodId) {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (old: any) =>
        old
          ? {
              ...old,
              payload: { ...old.payload, ...updatePurchaseIntentInput },
            }
          : old,
      );
    }

    return mutation.mutateAsync({
      params: { purchaseIntentId },
      body: updatePurchaseIntentInput,
    });
  };

  return { ...mutation, mutate: run, mutateAsync: run };
};
